import React, { useEffect, useMemo, useState } from "react";
import {
  getAllExercises,
  deleteExercise,
} from "../../../services/prepaPhysiqueServices";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import {
  FaPlus,
  FaSearch,
  FaExternalLinkAlt,
  FaInfoCircle,
} from "react-icons/fa";
import CreateExerciseModal from "./CreateExerciseModal";
import EditExerciseModal from "./EditExerciseModal";
import ExerciseInfoModal from "./ExerciseInfoModal";

const MAX_PREVIEW = 160;

function htmlToText(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}
function truncate(s, n = MAX_PREVIEW) {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

const ExerciseListSection = () => {
  const [exercises, setExercises] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [infoExercise, setInfoExercise] = useState(null);
  const [editExercise, setEditExercise] = useState(null);

  const [search, setSearch] = useState("");
  const [programmeFilter, setProgrammeFilter] = useState("All");

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const data = await getAllExercises();
      const arr = Array.isArray(data) ? data : [];
      setExercises(arr);
    } catch (error) {
      toast.error("Erreur lors du chargement des exercices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  // Programmes distincts pour le filtre
  const programmes = useMemo(() => {
    const setProg = new Set(
      (exercises || [])
        .map((e) => e?.programme)
        .filter((p) => p && String(p).trim().length > 0)
    );
    return ["All", ...Array.from(setProg).sort((a, b) => a.localeCompare(b))];
  }, [exercises]);

  // Filtrage
  useEffect(() => {
    const q = search.trim().toLowerCase();
    const list = (exercises || []).filter((ex) => {
      const inProgramme =
        programmeFilter === "All" || ex?.programme === programmeFilter;
      const inSearch =
        q.length === 0 ||
        [ex?.nom, ex?.programme]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      return inProgramme && inSearch;
    });
    setFiltered(list);
  }, [exercises, search, programmeFilter]);

  const handleDeleteExercise = async (id) => {
    if (window.confirm("Supprimer cet exercice ?")) {
      try {
        await deleteExercise(id);
        toast.success("Exercice supprimé.");
        fetchExercises();
      } catch (error) {
        toast.error("Erreur lors de la suppression.");
      }
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 transition-all duration-300">
      {/* Carte principale */}
      <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-6">
        <div className="mt-5 flex items-center justify-center">
          <Button
            text="Ajouter un exercice"
            icon={FaPlus}
            onClick={() => setIsCreateModalOpen(true)}
            className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
          />
        </div>

        {/* Filtres */}
        <div className="px-5 py-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Recherche (nom / programme)
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ex : Pompes, Gainage…"
                  className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Programme
              </label>
              <select
                value={programmeFilter}
                onChange={(e) => setProgrammeFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                {programmes.map((p) => (
                  <option key={p} value={p}>
                    {p === "All" ? "Tous" : p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Compteur */}
          <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
            Exercices affichés : {filtered.length} / {exercises.length}
          </div>
        </div>

        {/* Liste : Table desktop / Cards mobile */}
        <div className="px-5 pb-6 sm:px-8">
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-600 dark:text-slate-400">
              Aucun exercice ne correspond à vos filtres.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/40">
                <table className="min-w-[960px] w-full text-sm">
                  <thead className="bg-slate-50/90 dark:bg-slate-900/70">
                    <tr className="text-left text-slate-700 dark:text-slate-200">
                      <th className="px-4 py-3">Nom</th>
                      <th className="px-4 py-3">Programme</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Image</th>
                      <th className="px-4 py-3">Vidéo</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {filtered.map((ex) => {
                      const preview = truncate(
                        htmlToText(ex.description),
                        MAX_PREVIEW
                      );
                      return (
                        <tr
                          key={ex.id}
                          className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                            {ex.nom || "—"}
                          </td>
                          <td className="px-4 py-3">{ex.programme || "—"}</td>
                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {preview || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {ex.lien_photo ? (
                              <img
                                src={ex.lien_photo}
                                alt={ex.nom || "Illustration"}
                                className="w-24 h-24 object-cover rounded ring-1 ring-slate-200 dark:ring-slate-700"
                              />
                            ) : (
                              <span className="text-xs opacity-60">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {ex.lien_video ? (
                              <a
                                href={ex.lien_video}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 underline"
                              >
                                Voir la vidéo <FaExternalLinkAlt />
                              </a>
                            ) : (
                              <span className="text-xs opacity-60">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center items-center gap-2">
                              {/* Bouton Info */}
                              <button
                                type="button"
                                onClick={() => setInfoExercise(ex)}
                                className="w-8 h-8 flex items-center justify-center text-white rounded-md bg-indigo-600 p-2 shadow-sm transition duration-300 ease-in-out hover:bg-indigo-700"
                                title="Voir les détails"
                              >
                                <FaInfoCircle />
                              </button>
                              <ButtonEdit
                                onClick={() => {
                                  setEditExercise(ex);
                                  setIsEditModalOpen(true);
                                }}
                              />
                              <ButtonDelete
                                onClick={() => handleDeleteExercise(ex.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((ex) => {
                  const preview = truncate(
                    htmlToText(ex.description),
                    MAX_PREVIEW
                  );
                  return (
                    <div
                      key={ex.id}
                      className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/90 dark:bg-slate-900/40"
                    >
                      <div className="flex items-start gap-3">
                        <img
                          src={ex.lien_photo || "/img/no-image.jpg"}
                          alt={ex.nom || "Illustration"}
                          className="h-14 w-14 rounded object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                            {ex.nom || "—"}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {ex.programme || "—"}
                          </div>
                          {ex.lien_video && (
                            <div className="mt-1">
                              <a
                                href={ex.lien_video}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 underline"
                              >
                                Voir la vidéo <FaExternalLinkAlt />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {preview && (
                        <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
                          {preview}
                        </div>
                      )}

                      <div className="mt-3 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setInfoExercise(ex)}
                          className="w-8 h-8 flex items-center justify-center text-white rounded-md bg-indigo-600 p-2 shadow-sm transition duration-300 ease-in-out hover:bg-indigo-700"
                          title="Voir les détails"
                        >
                          <FaInfoCircle />
                        </button>
                        <ButtonEdit onClick={() => setInfoExercise(ex)} />
                        <ButtonDelete
                          onClick={() => handleDeleteExercise(ex.id)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modales */}
      {isCreateModalOpen && (
        <CreateExerciseModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refresh={fetchExercises}
        />
      )}

      {isEditModalOpen && editExercise && (
        <EditExerciseModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditExercise(null);
          }}
          exercise={editExercise}
          refresh={fetchExercises}
        />
      )}

      {infoExercise && (
        <ExerciseInfoModal
          isOpen={!!infoExercise}
          exercise={infoExercise}
          onClose={() => setInfoExercise(null)}
        />
      )}
    </div>
  );
};

export default ExerciseListSection;
